import type { GapProductV2 } from "@credit-acceptance/step6-bff-client";
import { z } from "zod";

// eslint-disable-next-line i18n-text/no-en -- hardcoded validation copy, matching Signers.schema.ts's convention
const REQUIRED_MESSAGE = "This is a required field.";

/** The "Select an Option" entry is itself a real, selectable list item (not a `SelectValue` placeholder) — matches the legacy dropdown's actual shape. Label is translated in the component, alongside the other Form Number options. */
export const GAP_FORM_NUMBER_SELECT_AN_OPTION = "__select_an_option__";

// ---------------------------------------------------------------------------
// gapInformationSchema — RHF + Zod for GapInformation.tsx, same pattern as
// VehicleServiceContractInformation.schema.ts. Values come from the BFF's
// `additionalProducts.gap` (`GapProductV2`); the TPAP-vs-non-TPAP split comes
// from `additionalProducts.metadata.tpapGap`. The BFF's single `addendumNo`
// is split into the UI's State Prefix/Number pair (see `splitAddendumNo`).
//
// Deliberately NOT modeled (no equivalent concept exists anywhere in this
// codebase): the "match original value on file" checks for Selling Price,
// Term/Coverage and Amount Financed (need CAPS/RISC data and a correction-
// letter feature flag), the AH-deal-status regression check on Addendum
// Number, and the Effective Date contractDate-relative range check.
//
// Addendum Number is required regardless of tpapGap — every other field here
// is only required while tpapGap. The State Prefix is optional: the BFF's
// addendum may carry no prefix at all (e.g. "#10001961067" splits to "#").
// ---------------------------------------------------------------------------
export const gapInformationSchema = z
  .object({
    // Never user-editable and never validated — just reflects whatever drives
    // the TPAP-vs-non-TPAP branching below (see the superRefine).
    tpapGap: z.boolean(),
    // Non-TPAP pure display counterpart to `tpapVendor` — never validated.
    vendorDisplay: z.string(),
    // The selected vendor's id as text. "Not directly enforced" per spec.
    tpapVendor: z.string(),
    // "Halves of the same addendum number" — only the number half is required.
    addendumStatePrefix: z.string(),
    addendumNumber: z.string(),
    // RHF needs null for a cleared controlled field; undefined restores its default.
    sellingPrice: z.number().nullable(),
    termCoverage: z.string(),
    amountFinanced: z.number().nullable(),
    effectiveDate: z.string(),
    formNumber: z.string(),
  })
  .superRefine((data, context) => {
    if (!data.addendumNumber) {
      context.addIssue({ code: "custom", path: ["addendumNumber"], message: REQUIRED_MESSAGE });
    }

    if (!data.tpapGap) {
      return;
    }
    if (data.sellingPrice === null) {
      context.addIssue({ code: "custom", path: ["sellingPrice"], message: REQUIRED_MESSAGE });
    }
    if (!data.termCoverage) {
      context.addIssue({ code: "custom", path: ["termCoverage"], message: REQUIRED_MESSAGE });
    }
    if (data.amountFinanced === null) {
      context.addIssue({ code: "custom", path: ["amountFinanced"], message: REQUIRED_MESSAGE });
    }
    if (!data.effectiveDate) {
      context.addIssue({ code: "custom", path: ["effectiveDate"], message: REQUIRED_MESSAGE });
    }
    if (!data.formNumber || data.formNumber === GAP_FORM_NUMBER_SELECT_AN_OPTION) {
      context.addIssue({ code: "custom", path: ["formNumber"], message: REQUIRED_MESSAGE });
    }
  });

export type GapInformationInput = z.infer<typeof gapInformationSchema>;

/**
 * Splits the BFF's single `addendumNo` into the UI's State Prefix/Number halves: any leading
 * letters/`#` form the prefix, the rest is the number. `joinAddendumNo` reverses it losslessly.
 * @param addendumNo The BFF's full addendum number, if any.
 * @returns {{ prefix: string; number: string }} The two halves.
 */
export function splitAddendumNo(addendumNo?: string | null): { prefix: string; number: string } {
  const [, prefix = "", number = ""] = /^([#A-Za-z]*)(.*)$/s.exec(addendumNo ?? "") ?? [];
  return { prefix, number };
}

/**
 * @param prefix The State Prefix half.
 * @param number The Number half.
 * @returns {string} The BFF's single `addendumNo`.
 */
export function joinAddendumNo(prefix: string, number: string): string {
  return `${prefix}${number}`;
}

/**
 * Builds RHF defaultValues from the BFF's `additionalProducts.gap`.
 * @param gap The dashboard response's GAP product, if any.
 * @param tpapGap Whether the GAP is a third-party (TPAP) product — `additionalProducts.metadata.tpapGap`.
 * @returns {GapInformationInput} Seed values for `useForm`'s `defaultValues`.
 */
export function buildGapDefaultValues(gap?: GapProductV2, tpapGap = false): GapInformationInput {
  const addendum = splitAddendumNo(gap?.addendumNo);
  return {
    tpapGap,
    vendorDisplay: gap?.vendorName ?? "",
    tpapVendor: typeof gap?.vendorId === "number" ? String(gap.vendorId) : "",
    addendumStatePrefix: addendum.prefix,
    addendumNumber: addendum.number,
    // eslint-disable-next-line unicorn/no-null -- RHF's controlled empty value
    sellingPrice: gap?.sellingPrice ?? null,
    termCoverage: typeof gap?.term === "number" ? String(gap.term) : "",
    // eslint-disable-next-line unicorn/no-null -- RHF's controlled empty value
    amountFinanced: gap?.amountFinanced ?? null,
    effectiveDate: gap?.effectiveDate ?? "",
    formNumber: gap?.formNo ?? GAP_FORM_NUMBER_SELECT_AN_OPTION,
  };
}

/**
 * Maps the form back to the save request's GAP node (`gap` is written in full).
 * Sends the chosen vendor's id; the original name is kept only while the vendor is unchanged.
 * @param values Current form values.
 * @param original The dashboard's GAP product the form was seeded from.
 * @returns {GapProductV2} The GAP product to save.
 */
export function toGapSaveRequest(values: GapInformationInput, original?: GapProductV2): GapProductV2 {
  const hasForm = values.formNumber && values.formNumber !== GAP_FORM_NUMBER_SELECT_AN_OPTION;
  const vendorId = values.tpapVendor ? Number(values.tpapVendor) : (original?.vendorId ?? undefined);
  return {
    vendorId,
    vendorName: vendorId === original?.vendorId ? original?.vendorName || undefined : undefined,
    addendumNo: joinAddendumNo(values.addendumStatePrefix, values.addendumNumber) || undefined,
    effectiveDate: values.effectiveDate || undefined,
    sellingPrice: values.sellingPrice ?? undefined,
    term: values.termCoverage.trim() === "" ? undefined : Number(values.termCoverage),
    amountFinanced: values.amountFinanced ?? undefined,
    formNo: hasForm ? values.formNumber : undefined,
  };
}
